import { Injectable, PLATFORM_ID, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { LocalStorageService } from '../../shared/local-storage.service';
import { AccountService, PaymentMethod } from '../../shared/account.service';
import { PersonalInformation, DeliveryAddress } from '../signup/signup.types';
import {
  DeliveryOptionsResponseItem,
  AccountSettings
} from '../../shared/types/account.types';
import { SignUpSteps } from '../../shared/types/common.enums';
import { LOCALSTORAGE_KEYS } from '../../shared/constants/databases';
import { SignupFlowStepsRoutes } from './signup-v2.constants';
import { SignalsStoreService } from '../../shared/signals-store.service';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { SubscriptionPayload } from '../../settings/account/subscriptions/subscriptions.types';
import { NavegoAddress } from '../../shared/address.service';

/**
 * Signup Step State Interface
 */
export interface SignupStepState {
  currentStep: SignUpSteps;
  completedSteps: SignUpSteps[];
  canProceed: boolean;
}

/**
 * Delivery Information State
 */
export interface DeliveryInformation {
  address: NavegoAddress | null;
  deliveryAddress: DeliveryAddress | null;
  deliveryDay: DeliveryOptionsResponseItem | null;
  deliveryTimeWindow: number | null;
  deliveryOption: { id: number; name: string } | null;
  deliveryOptions: DeliveryOptionsResponseItem[];
}

/**
 * Payment Information State
 */
export interface PaymentInformation {
  paymentMethod: PaymentMethod | null;
  isValid: boolean;
}

/**
 * Signup Form Validation State
 */
export interface SignupValidation {
  farmbox: boolean;
  delivery: boolean;
  personal: boolean;
  payment: boolean;
}

/**
 * Complete Signup State
 */
export interface SignupState {
  stepState: SignupStepState;
  farmbox: SubscriptionPayload;
  delivery: DeliveryInformation;
  personal: PersonalInformation | null;
  payment: PaymentInformation;
  validation: SignupValidation;
  accountSettings: AccountSettings | null;
  isLoading: boolean;
  error: string | null;
  accountCreated: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SignupV2Service {
  private localStorageService = inject(LocalStorageService);
  private accountService = inject(AccountService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private signalsStoreService = inject(SignalsStoreService);

  // ==================== STEP STATE ====================
  #currentStep: WritableSignal<SignUpSteps> = signal(SignUpSteps.selectYourFarmbox);
  currentStep: Signal<SignUpSteps> = computed(() => this.#currentStep());

  #completedSteps: WritableSignal<SignUpSteps[]> = signal([]);
  completedSteps: Signal<SignUpSteps[]> = computed(() => this.#completedSteps());

  #accountCreated: WritableSignal<boolean> = signal(false);
  accountCreated: Signal<boolean> = computed(() => this.#accountCreated());

  // Computed step state
  stepState: Signal<SignupStepState> = computed(() => ({
    currentStep: this.#currentStep(),
    completedSteps: this.#completedSteps(),
    canProceed: this.canProceedToNextStep()
  }));

  // ==================== FARMBOX SELECTION ====================
  #farmboxSelection: WritableSignal<SubscriptionPayload> = signal({
    isSignup: true,
    startDate: null,
    variantId: 0,
    packageId: 0,
    frequencyId: 0,
    quantity: 1
  });
  farmboxSelection: Signal<SubscriptionPayload> = computed(() => this.#farmboxSelection());

  // ==================== DELIVERY INFORMATION ====================
  #deliveryInformation: WritableSignal<DeliveryInformation> = signal({
    address: null,
    deliveryAddress: null,
    deliveryDay: null,
    deliveryTimeWindow: null,
    deliveryOption: null,
    deliveryOptions: []
  });
  deliveryInformation: Signal<DeliveryInformation> = computed(() => this.#deliveryInformation());

  // ==================== PERSONAL INFORMATION ====================
  #personalInformation: WritableSignal<PersonalInformation | null> = signal(null);
  personalInformation: Signal<PersonalInformation | null> = computed(() => this.#personalInformation());

  // ==================== PAYMENT INFORMATION ====================
  #paymentInformation: WritableSignal<PaymentInformation> = signal({
    paymentMethod: null,
    isValid: false
  });
  paymentInformation: Signal<PaymentInformation> = computed(() => this.#paymentInformation());

  // ==================== ACCOUNT SETTINGS ====================
  #accountSettings: WritableSignal<AccountSettings | null> = signal(null);
  accountSettings: Signal<AccountSettings | null> = computed(() => this.#accountSettings());

  // ==================== VALIDATION STATE ====================
  #validation: WritableSignal<SignupValidation> = signal({
    farmbox: false,
    delivery: false,
    personal: false,
    payment: false
  });
  validation: Signal<SignupValidation> = computed(() => this.#validation());

  // ==================== LOADING & ERROR STATE ====================
  #isLoading: WritableSignal<boolean> = signal(false);
  isLoading: Signal<boolean> = computed(() => this.#isLoading());
  setIsLoading(isLoading: boolean): void {
    this.#isLoading.set(isLoading);
  }

  #error: WritableSignal<string | null> = signal(null);
  error: Signal<string | null> = computed(() => this.#error());
  setError(error: string | null): void {
    this.#error.set(error);
  }

  // ==================== COMPUTED STATE ====================
  /**
   * Complete signup state computed signal
   */
  signupState: Signal<SignupState> = computed(() => ({
    stepState: this.stepState(),
    farmbox: this.farmboxSelection(),
    delivery: this.deliveryInformation(),
    personal: this.personalInformation(),
    payment: this.paymentInformation(),
    validation: this.validation(),
    accountSettings: this.accountSettings(),
    isLoading: this.isLoading(),
    error: this.error(),
    accountCreated: this.#accountCreated()
  }));

  /**
   * Check if current step is valid and can proceed
   */
  canProceedToNextStep(): boolean {
    const step = this.#currentStep();
    const validation = this.#validation();

    switch (step) {
      case SignUpSteps.selectYourFarmbox:
        return validation.farmbox;
      case SignUpSteps.deliveryInformation:
        return validation.delivery;
      case SignUpSteps.personalInformation:
        return validation.personal;
      case SignUpSteps.paymentInformation:
        return validation.payment;
      default:
        return false;
    }
  }

  /**
   * Check if a specific step is completed
   */
  isStepCompleted(step: SignUpSteps): boolean {
    return this.#completedSteps().includes(step);
  }

  // ==================== STEP MANAGEMENT ====================

  /**
   * Set current step
   */
  setCurrentStep(step: SignUpSteps): void {
    this.#currentStep.set(step);
    this.localStorageService.set(LOCALSTORAGE_KEYS.SIGNUP_CURRENT_STEP, step);
  }

  /**
   * Mark step as completed
   */
  completeStep(step: SignUpSteps): void {
    const completed = this.#completedSteps();
    if (!completed.includes(step)) {
      this.#completedSteps.set([...completed, step]);
    }
  }

  /**
   * Mark account as created - called after successful account creation
   * This prevents backward navigation from payment step
   */
  setAccountCreated(created: boolean): void {
    this.#accountCreated.set(created);
    if (created) {
      this.localStorageService.set(LOCALSTORAGE_KEYS.ACCOUNT_CREATED_IN_SIGNUP, true);
    } else {
      this.localStorageService.remove(LOCALSTORAGE_KEYS.ACCOUNT_CREATED_IN_SIGNUP);
    }
  }

  /**
   * Check if account was created during signup flow
   */
  isAccountCreated(): boolean {
    return this.#accountCreated();
  }

  /**
   * Check if backward navigation is allowed from current step
   */
  canNavigateBackward(): boolean {
    // If account has been created, no backward navigation allowed
    if (this.#accountCreated()) {
      return false;
    }

    const current = this.#currentStep();
    // Can go back if we're past the first step
    return current > SignUpSteps.selectYourFarmbox;
  }

  /**
   * Navigate to next step
   */
  nextStep(): boolean {
    if (!this.canProceedToNextStep()) {
      return false;
    }

    const current = this.#currentStep();
    this.completeStep(current);

    const nextStep = current + 1;
    if (nextStep <= SignUpSteps.paymentInformation) {
      this.setCurrentStep(nextStep as SignUpSteps);
      return true;
    }
    return false;
  }

  /**
   * Navigate to previous step
   * Returns false if navigation is not allowed (account already created)
   */
  previousStep(): boolean {
    // Block backward navigation if account has been created
    if (!this.canNavigateBackward()) {
      return false;
    }

    const current = this.#currentStep();
    if (current > SignUpSteps.selectYourFarmbox) {
      const prevStep = current - 1;
      this.setCurrentStep(prevStep as SignUpSteps);
      return true;
    }
    return false;
  }

  /**
   * Navigate to specific step (if allowed)
   */
  goToStep(step: SignUpSteps): boolean {
    // If account was created, prevent going back to any previous step
    if (this.#accountCreated() && step < this.#currentStep()) {
      return false;
    }

    // Allow going back to completed steps or forward if previous steps are completed
    if (this.isStepCompleted(step) || this.canNavigateToStep(step)) {
      this.setCurrentStep(step);
      return true;
    }
    return false;
  }

  /**
   * Check if can navigate to a specific step
   */
  private canNavigateToStep(step: SignUpSteps): boolean {
    const completed = this.#completedSteps();

    // Can navigate to step if all previous steps are completed
    for (let i = SignUpSteps.selectYourFarmbox; i < step; i++) {
      if (!completed.includes(i as SignUpSteps)) {
        return false;
      }
    }
    return true;
  }

  // ==================== FARMBOX SELECTION ====================

  /**
   * Set farmbox selection
   */
  setFarmboxSelection(selection: Partial<SubscriptionPayload>): void {
    this.#farmboxSelection.update(current => ({
      ...current,
      ...selection
    }));

    // Save to localStorage
    if (selection.variantId) {
      this.localStorageService.set(LOCALSTORAGE_KEYS.PRODUCTS_TO_SUBSCRIBE, selection);
    }

    // Update validation
    this.updateValidation('farmbox', this.isFarmboxSelectionValid());
  }

  /**
   * Check if farmbox selection is valid
   */
  private isFarmboxSelectionValid(): boolean {
    const selection = this.#farmboxSelection();
    return !!(
      selection.variantId &&
      selection.frequencyId
    );
  }

  // ==================== DELIVERY INFORMATION ====================

  /**
   * Set delivery address
   */
  setDeliveryAddress(address: NavegoAddress, deliveryAddress: DeliveryAddress): void {
    this.#deliveryInformation.update(current => ({
      ...current,
      address,
      deliveryAddress
    }));

    // Update validation
    this.updateValidation('delivery', this.isDeliveryInformationValid());
  }

  /**
   * Set delivery day and time window
   */
  setDeliveryDayAndTime(
    deliveryDay: DeliveryOptionsResponseItem,
    timeWindow: number | null
  ): void {
    this.#deliveryInformation.update(current => ({
      ...current,
      deliveryDay,
      deliveryTimeWindow: timeWindow
    }));

    // Update validation
    this.updateValidation('delivery', this.isDeliveryInformationValid());
  }

  /**
   * Set delivery option (Home Delivery, Pick Up, EBT/Snap)
   */
  setDeliveryOption(option: { id: number; name: string }): void {
    this.#deliveryInformation.update(current => ({
      ...current,
      deliveryOption: option
    }));
  }

  /**
   * Load delivery options
   */
  loadDeliveryOptions(zoneId: number): Observable<DeliveryOptionsResponseItem[]> {
    this.#isLoading.set(true);
    this.#error.set(null);

    return this.accountService.fetchDeliveryOptions(zoneId).pipe(
      tap(options => {
        this.#deliveryInformation.update(current => ({
          ...current,
          deliveryOptions: options
        }));
        this.#isLoading.set(false);
      }),
      catchError(error => {
        this.#isLoading.set(false);
        this.#error.set('Failed to load delivery options');
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if delivery information is valid
   */
  private isDeliveryInformationValid(): boolean {
    const delivery = this.#deliveryInformation();
    const hasAddress = !!delivery.address && !!delivery.deliveryAddress;
    const hasDeliveryDay = !!delivery.deliveryDay;
    const hasTimeWindow = !delivery.deliveryDay?.allowTimeWindow ||
      (delivery.deliveryDay.allowTimeWindow && delivery.deliveryTimeWindow !== null);

    return hasAddress && hasDeliveryDay && hasTimeWindow;
  }

  // ==================== PERSONAL INFORMATION ====================

  /**
   * Set personal information
   */
  setPersonalInformation(personalInfo: PersonalInformation): void {
    this.#personalInformation.set(personalInfo);
    this.updateValidation('personal', this.isPersonalInformationValid());
  }

  /**
   * Update personal information partially
   */
  updatePersonalInformation(updates: Partial<PersonalInformation>): void {
    const current = this.#personalInformation();
    if (current) {
      this.#personalInformation.set({ ...current, ...updates });
    } else {
      // Initialize with updates if null
      this.#personalInformation.set(updates as PersonalInformation);
    }
    this.updateValidation('personal', this.isPersonalInformationValid());
  }

  /**
   * Load account settings (for dropdowns, etc.)
   */
  loadAccountSettings(): Observable<AccountSettings> {
    this.#isLoading.set(true);
    this.#error.set(null);

    return this.accountService.getAccountSettings().pipe(
      tap(settings => {
        this.#accountSettings.set(settings);
        this.#isLoading.set(false);
      }),
      catchError(error => {
        this.#isLoading.set(false);
        this.#error.set('Failed to load account settings');
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if personal information is valid
   */
  private isPersonalInformationValid(): boolean {
    const personal = this.#personalInformation();
    if (!personal) return false;

    return !!(
      personal.email &&
      personal.firstName &&
      personal.lastName &&
      personal.password &&
      personal.phone &&
      personal.password.length >= 8
    );
  }

  // ==================== PAYMENT INFORMATION ====================

  /**
   * Set payment method
   */
  setPaymentMethod(paymentMethod: PaymentMethod): void {
    this.#paymentInformation.update(current => ({
      ...current,
      paymentMethod
    }));
    this.updateValidation('payment', this.isPaymentMethodValid());
  }

  /**
   * Set payment validation state
   */
  setPaymentValidation(isValid: boolean): void {
    this.#paymentInformation.update(current => ({
      ...current,
      isValid
    }));
    this.updateValidation('payment', isValid);
  }

  /**
   * Check if payment method is valid
   */
  private isPaymentMethodValid(): boolean {
    const payment = this.#paymentInformation();
    return payment.isValid && !!payment.paymentMethod;
  }

  // ==================== VALIDATION ====================

  /**
   * Update validation for a specific step
   */
  setStepValidation(step: keyof SignupValidation, isValid: boolean): void {
    this.updateValidation(step, isValid);
  }

  /**
   * Update validation for a specific step
   */
  private updateValidation(step: keyof SignupValidation, isValid: boolean): void {
    this.#validation.update(current => ({
      ...current,
      [step]: isValid
    }));
  }

  /**
   * Reset validation for a step
   */
  resetValidation(step?: keyof SignupValidation): void {
    if (step) {
      this.#validation.update(current => ({
        ...current,
        [step]: false
      }));
    } else {
      this.#validation.set({
        farmbox: false,
        delivery: false,
        personal: false,
        payment: false
      });
    }
  }

  // ==================== STATE PERSISTENCE ====================

  /**
   * Load state from localStorage
   */
  loadStateFromStorage(): void {
    const currentStep = this.localStorageService.get<number>(LOCALSTORAGE_KEYS.SIGNUP_CURRENT_STEP);
    if (currentStep !== null) {
      this.setCurrentStep(currentStep as SignUpSteps);
    }

    const product = this.localStorageService.get(LOCALSTORAGE_KEYS.PRODUCTS_TO_SUBSCRIBE);
    if (product) {
      // Restore farmbox selection if exists
      // Note: You'll need to load the product details separately
    }

    // Restore account creation flag
    const accountCreated = this.localStorageService.get(LOCALSTORAGE_KEYS.ACCOUNT_CREATED_IN_SIGNUP);
    if (accountCreated) {
      this.#accountCreated.set(true);
    }
  }

  /**
   * Clear signup state
   */
  clearState(): void {
    this.#currentStep.set(SignUpSteps.selectYourFarmbox);
    this.#completedSteps.set([]);
    this.#accountCreated.set(false);
    this.#farmboxSelection.set({
      variantId: 0,
      packageId: 0,
      frequencyId: 0,
      quantity: 1,
      startDate: null,
      isSignup: true
    });
    this.#deliveryInformation.set({
      address: null,
      deliveryAddress: null,
      deliveryDay: null,
      deliveryTimeWindow: null,
      deliveryOption: null,
      deliveryOptions: []
    });
    this.#personalInformation.set(null);
    this.#paymentInformation.set({
      paymentMethod: null,
      isValid: false
    });
    this.#validation.set({
      farmbox: false,
      delivery: false,
      personal: false,
      payment: false
    });
    this.#error.set(null);

    // Clear localStorage
    this.localStorageService.remove(LOCALSTORAGE_KEYS.SIGNUP_CURRENT_STEP);
    this.localStorageService.remove(LOCALSTORAGE_KEYS.PRODUCTS_TO_SUBSCRIBE);
    this.localStorageService.remove(LOCALSTORAGE_KEYS.ACCOUNT_CREATED_IN_SIGNUP);
  }

  loadExistingProcess(fromStep: SignUpSteps): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Load account created flag from localStorage
    this.loadStateFromStorage();

    if (this.signalsStoreService.hasSession()) {
      if (this.signalsStoreService.sessionSignal()?.settings?.hasPaymentMethod)
        this.router.navigate(['/shop']);
      else
        this.router.navigate(['/signup/payment']);
      return;
    }

    const existingSelectedProduct: SubscriptionPayload | null = this.localStorageService
      .get(LOCALSTORAGE_KEYS.PRODUCTS_TO_SUBSCRIBE);

    if (existingSelectedProduct) {
      this.setFarmboxSelection(existingSelectedProduct);
    }

    const currentStoredStep = this.#currentStep();

    // Check if user explicitly navigated to this step (current step matches target step)
    // This happens when goBack() explicitly set the current step before navigating
    // If so, allow them to stay on the requested step instead of auto-redirecting
    if (currentStoredStep === fromStep && !this.#accountCreated()) {
      // User explicitly navigated to this step, don't auto-redirect
      return;
    }

    // Legacy backward navigation check (for cases where current step wasn't updated)
    const isNavigatingBackward = currentStoredStep > fromStep;
    if (isNavigatingBackward && !this.#accountCreated()) {
      // Update current step to the step user is navigating to
      this.setCurrentStep(fromStep);
      // Don't auto-redirect, let user stay on the step they navigated to
      return;
    }

    const fnStepToLoad: () => SignUpSteps = () => {
      return this.signalsStoreService.hasSession()
        ? fromStep ?? SignUpSteps.paymentInformation
        : existingSelectedProduct?.variantId ? SignUpSteps.deliveryInformation : SignUpSteps.selectYourFarmbox;
    }

    let stepToLoad = fnStepToLoad();

    if (fromStep === SignUpSteps.personalInformation) {
      if (this.stepState().completedSteps.includes(SignUpSteps.deliveryInformation)) {
        stepToLoad = SignUpSteps.personalInformation;
      }
    }



    this.router.navigate([`/signup/${SignupFlowStepsRoutes.get(stepToLoad)}`]);
  }

}
